import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Sparkles, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { createContribution } from "@/lib/api";
import { DOMAINS } from "@/lib/domains";

/**
 * Reusable, controlled dialog that lets any logged-in user PROPOSE A NEW TOPIC.
 * Submissions are saved as a `new_topic` contribution with status="pending" and
 * appear in the admin Moderation queue for approval before anything is published.
 *
 * Backend `ContributionBody` only carries { type, title, content, ... }, so the
 * chosen domain/category and the "why it matters" note are folded into `content`
 * using a stable, human-readable template the moderator can read at a glance.
 */
export function ProposeTopicDialog({ open, onOpenChange, onSubmitted }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [domain, setDomain] = useState("");
  const [why, setWhy] = useState("");
  const [claims, setClaims] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setTitle(""); setDomain(""); setWhy(""); setClaims("");
  };

  const close = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast("Sign in to suggest a mystery", { description: "Proposals are tied to your account and reviewed before publishing." });
      onOpenChange(false);
      navigate("/login");
      return;
    }
    if (!title.trim() || !why.trim()) {
      toast("A title and a short reason are required");
      return;
    }
    // Fold structured fields into the contribution `content` for moderator review.
    const content = [
      domain ? `Proposed domain: ${domain}` : null,
      `Why it belongs in the archive:\n${why.trim()}`,
      claims.trim() ? `Key claims / evidence to explore:\n${claims.trim()}` : null,
    ].filter(Boolean).join("\n\n");

    setSaving(true);
    try {
      await createContribution({ type: "new_topic", title: title.trim(), content });
      toast("Proposal submitted — pending review", {
        description: "A moderator reviews every proposal before it enters the archive.",
      });
      close(false);
      onSubmitted && onSubmitted();
    } catch (err) {
      if (err?.response?.status === 401) {
        toast("Your session expired — please sign in again");
        onOpenChange(false);
        navigate("/login");
      } else {
        toast("Could not submit your proposal");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="codex-glass-strong max-w-lg border-white/10" data-testid="propose-topic-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif text-xl">
            <Sparkles className="h-5 w-5 text-gold-light" /> Suggest a Mystery
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Suggest a mystery, event, or thread for the archive. Every proposal is reviewed by a
            moderator before it is published.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="mt-1 space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground/70">Topic title</label>
            <input
              data-testid="propose-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. The Nimrud Lens"
              className="mt-1 h-10 w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 text-sm focus:border-gold-light/40 focus:outline-none"
            />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground/70">Domain (optional)</label>
            <Select value={domain} onValueChange={setDomain}>
              <SelectTrigger data-testid="propose-domain-select" className="mt-1 h-10 border-white/10 bg-white/[0.03] focus:ring-gold-light/30">
                <SelectValue placeholder="Choose a domain…" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {DOMAINS.map((d) => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground/70">Why it belongs in the archive</label>
            <textarea
              data-testid="propose-why-input"
              value={why}
              onChange={(e) => setWhy(e.target.value)}
              placeholder="What is the mystery, and why is it worth documenting?"
              className="mt-1 h-24 w-full resize-none rounded-xl border border-white/10 bg-white/[0.03] p-3 text-sm focus:border-gold-light/40 focus:outline-none"
            />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground/70">Key claims or sources (optional)</label>
            <textarea
              data-testid="propose-claims-input"
              value={claims}
              onChange={(e) => setClaims(e.target.value)}
              placeholder="Notable claims, evidence, people, or sources a researcher should start with…"
              className="mt-1 h-20 w-full resize-none rounded-xl border border-white/10 bg-white/[0.03] p-3 text-sm focus:border-gold-light/40 focus:outline-none"
            />
          </div>

          <button
            type="submit"
            data-testid="propose-submit-button"
            disabled={saving}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold text-[color:var(--codex-gold-ink)] transition-opacity disabled:opacity-60"
            style={{ backgroundColor: "#D8B66A" }}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Submit for review
          </button>

          <p className="flex items-center justify-center gap-1.5 text-center text-[11px] text-muted-foreground/70">
            <ShieldCheck className="h-3.5 w-3.5" /> Nothing is published until a moderator approves it.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
